/**
 * Sandboxed HTML doc viewer — srcdoc iframe, no network, relative-link nav,
 * auto-height, and text-selection reporting via postMessage.
 * Same isolation posture as Hub / org-chart; used by Docs tab and Library HTML docs.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";

const c = brainTokens.color;

const BRIDGE_SCRIPT = `
<script>
(function () {
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest ? e.target.closest("a") : null;
    if (!a) return;
    var href = a.getAttribute("href");
    if (!href) return;
    if (/^(https?:|mailto:|tel:|#)/i.test(href)) return;
    e.preventDefault();
    e.stopPropagation();
    parent.postMessage({ type: "brain-doc-nav", href: href }, "*");
  }, true);

  // Auto-height: report the document height so the parent can render seamlessly.
  var report = function () {
    var h = Math.max(
      document.documentElement ? document.documentElement.scrollHeight : 0,
      document.body ? document.body.scrollHeight : 0
    );
    parent.postMessage({ type: "brain-doc-height", height: h }, "*");
  };
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(report).observe(document.documentElement);
  }
  window.addEventListener("load", report);
  setTimeout(report, 50);
  setTimeout(report, 400);

  // Selection: report highlighted text so the parent can offer "add feedback".
  document.addEventListener("mouseup", function () {
    setTimeout(function () {
      var sel = window.getSelection ? String(window.getSelection()) : "";
      parent.postMessage({ type: "brain-doc-select", text: sel.trim() }, "*");
    }, 0);
  });
})();
</script>
`;

// Injected into seamless docs so v1 pages render white + chrome-less in the tab.
// v2 (brain-doc-version 2) pages have none of these hooks — it's a no-op there.
const NORMALIZE_STYLE = `
<style data-brain-normalize>
  html, body { background: #fff !important; }
  body { max-width: none !important; }
  .page { max-width: none !important; padding: 0 0 32px !important; margin: 0 !important; }
  header.doc { display: none !important; }
  body > footer { display: none !important; }
</style>
`;

function injectNormalize(html: string): string {
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => `${m}\n${NORMALIZE_STYLE}`);
  return `${NORMALIZE_STYLE}${html}`;
}

function injectBridge(html: string): string {
  if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, `${BRIDGE_SCRIPT}</body>`);
  return `${html}${BRIDGE_SCRIPT}`;
}

export function SandboxedDocViewer({
  html,
  title,
  onNavigate,
  onSelect,
  height = "70vh",
  seamless = false,
}: {
  html: string;
  title?: string;
  /** Called for relative hrefs (./docs/foo.html, docs/foo.html, foo.html). */
  onNavigate?: (href: string) => void;
  /** Called with the currently highlighted text ("" when selection cleared). */
  onSelect?: (text: string) => void;
  height?: number | string;
  /** Borderless + auto-height: the doc reads as part of the page (hub feel). */
  seamless?: boolean;
}) {
  const srcDoc = useMemo(() => injectBridge(seamless ? injectNormalize(html) : html), [html, seamless]);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const [autoHeight, setAutoHeight] = useState<number | null>(null);
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const handler = (ev: MessageEvent) => {
      // Only accept messages from OUR iframe (several viewers can coexist).
      if (frameRef.current && ev.source !== frameRef.current.contentWindow) return;
      const data = ev.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "brain-doc-nav" && typeof data.href === "string") {
        onNavigateRef.current?.(data.href);
      } else if (data.type === "brain-doc-height" && typeof data.height === "number") {
        setAutoHeight(Math.min(Math.max(data.height, 200), 20000));
      } else if (data.type === "brain-doc-select" && typeof data.text === "string") {
        onSelectRef.current?.(data.text);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <Box
      ref={frameRef}
      component="iframe"
      srcDoc={srcDoc}
      title={title ?? "Document"}
      // allow-scripts without allow-same-origin: opaque origin, postMessage only
      sandbox="allow-scripts allow-top-navigation-by-user-activation"
      sx={
        seamless
          ? {
              width: "100%",
              height: autoHeight ? `${autoHeight + 2}px` : height,
              border: "none",
              display: "block",
              background: "transparent",
            }
          : {
              width: "100%",
              height: autoHeight ? `${autoHeight + 2}px` : height,
              minHeight: 420,
              border: `1px solid ${c.border}`,
              borderRadius: "8px",
              background: "#fff",
            }
      }
    />
  );
}

/** Resolve a relative doc href against the current doc key (S3-style path). */
export function resolveDocHref(currentKey: string, href: string): string | null {
  const cleaned = href.split("#")[0]?.split("?")[0]?.trim() ?? "";
  if (!cleaned || /^(https?:|mailto:|tel:)/i.test(cleaned)) return null;
  if (cleaned.startsWith("#")) return null;

  const parts = currentKey.split("/");
  parts.pop(); // drop filename
  const baseDir = parts.join("/");

  const stack = baseDir ? baseDir.split("/") : [];
  for (const seg of cleaned.replace(/^\.\//, "").split("/")) {
    if (!seg || seg === ".") continue;
    if (seg === "..") {
      if (stack.length) stack.pop();
      continue;
    }
    stack.push(seg);
  }
  return stack.join("/");
}
